import { LabelDecoration } from "PosterEditor/model/poster-types";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import styles from "./toolbox-label-style.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxReverseGeocodeSuggestions } from "PosterEditor/sidebar/toolboxes/pre-made-toolboxes/toolbox-reverse-geocode-suggestions/toolbox-reverse-geocode-suggestions";
import { MapLabel } from "PosterEditor/model/poster-project";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { legendTintColors } from "PosterEditor/model/poster-project";
import { defaultLabelStyle } from "PosterEditor/model/default-project-style";
import { useState, useEffect } from "react";
import { DefaultMapLabel } from "PosterEditor/model/default-project-style";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { ToolboxComponentFontPicker as FontPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-font-picker";
import {
  fontNames,
  MapLabelDecorationStyle,
} from "PosterEditor/model/poster-project";

import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";
import { UIIconButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import {
  DeleteButton,
  BackButton,
} from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";

interface ToolboxLabelStyleProps {
  label: MapLabel;
}

export const ToolboxMapLabelStyle = (props: ToolboxLabelStyleProps) => {
  const {
    project,
    isTemplateEditing,
    setMapLabel: saveLabelToProject,
    getMapLabel,
    removeMapLabel,
  } = usePosterProject();
  const { setEditingElement } = useEditorUI();

  const [label, setLabel] = useState<MapLabel>(props.label);

  useEffect(() => {
    const labelID = props.label.id;
    const label = getMapLabel(labelID) ?? DefaultMapLabel;
    setLabel(label);
  }, [getMapLabel, props.label?.id, props.label.coordinate]);

  const updateLabel = (
    text?: string,
    style?: {
      backgroundColor?: string;
      backgroundOpacity?: number;
      pointerColor?: string;
      pointerThickness?: number;
      borderRadius?: number;
      padding?: number;
      textColor?: string;
      fontSize?: number;
      fontName?: string;
      decorationStyle?: MapLabelDecorationStyle;
    }
  ) => {
    if (!label) {
      return;
    }
    const updatedLabel: MapLabel = {
      ...label,
      coordinate: label.coordinate,
      text: text ?? label.text,
      style: {
        ...label.style,
        offset: label.style.offset ?? { x: 0, y: 0 },
        backgroundColor: style?.backgroundColor ?? label.style.backgroundColor,
        backgroundOpacity:
          style?.backgroundOpacity ?? label.style.backgroundOpacity,
        decorationStyle: style?.decorationStyle ?? label.style.decorationStyle,
        pointerColor: style?.pointerColor ?? label.style.pointerColor,
        pointerThickness:
          style?.pointerThickness ?? label.style.pointerThickness,
        borderRadius: style?.borderRadius ?? label.style.borderRadius,
        padding: style?.padding ?? label.style.padding,
        textColor: style?.textColor ?? label.style.textColor,
        fontSize: style?.fontSize ?? label.style.fontSize,
        font: style?.fontName ?? label.style.font,
      },
    };
    setLabel(updatedLabel);
    saveLabelToProject(updatedLabel);
  };

  return (
    <div className={styles.container}>
      <Header
        label={props.label}
        onClickBackButton={() => setEditingElement(undefined)}
        onClickDelete={() => {
          removeMapLabel(props.label);
          setEditingElement(undefined);
        }}
      />
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          {(isTemplateEditing || project.editingPermissions.mapLabel.text) && (
            <>
              <TextContentControl
                coordinate={props.label.coordinate}
                text={label?.text ?? ""}
                onChangeText={updateLabel}
                onClickDeleteLabel={() => {
                  if (label) {
                    setEditingElement(undefined);
                    removeMapLabel(label);
                  }
                }}
              />
              <Divider />
            </>
          )}
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.font ||
            project.editingPermissions.mapLabel.color ||
            project.editingPermissions.mapLabel.size) && (
            <>
              <TextStyleControl
                font={label?.style.font ?? "Arial"}
                textColor={label?.style.textColor ?? "black"}
                fontSize={label?.style.fontSize ?? 12}
                onFontChange={(font) =>
                  updateLabel(undefined, { fontName: font })
                }
                onChangeTextColor={(color) =>
                  updateLabel(undefined, { textColor: color })
                }
                onChangeFontSize={(fontSize) =>
                  updateLabel(undefined, { fontSize })
                }
              />
              <Divider />
            </>
          )}
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.backgroundColor ||
            project.editingPermissions.mapLabel.backgroundOpacity ||
            project.editingPermissions.mapLabel.backgroundPadding) && (
            <>
              <BackgroundControl
                backgroundColor={label?.style.backgroundColor ?? "white"}
                backgroundOpacity={
                  label?.style.backgroundOpacity ??
                  defaultLabelStyle.backgroundOpacity ??
                  0.5
                }
                padding={label?.style.padding ?? 5}
                onChangeBackgroundColor={(backgroundColor) => {
                  updateLabel(undefined, { backgroundColor });
                }}
                onChangeBackgroundOpacity={(backgroundOpacity) => {
                  updateLabel(undefined, { backgroundOpacity });
                }}
                onChangePadding={(padding) =>
                  updateLabel(undefined, { padding })
                }
              />
              <Divider />
            </>
          )}
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.decoration) && (
            <>
              <DecorationControl
                style={
                  label?.style.decorationStyle ??
                  defaultLabelStyle.decorationStyle
                }
                onChangeStyle={(decorationStyle) =>
                  updateLabel(undefined, { decorationStyle })
                }
              />
              <Divider />
            </>
          )}
          {(isTemplateEditing ||
            project.editingPermissions.mapLabel.pointer) && (
            <PointerControl
              color={label?.style.pointerColor ?? "black"}
              thickness={label?.style.pointerThickness ?? 0}
              onChangeColor={(color) =>
                updateLabel(undefined, { pointerColor: color })
              }
              onChangeThickness={(thickness) =>
                updateLabel(undefined, { pointerThickness: thickness })
              }
            />
          )}
        </div>
      </Toolbox>
    </div>
  );
};

interface HeaderProps {
  label: MapLabel;
  onClickBackButton: () => void;
  onClickDelete: () => void;
}

const Header = (props: HeaderProps) => {
  const selectedCoordinate = props.label.coordinate;

  const latLongString = `${selectedCoordinate.lat.toFixed(
    6
  )}, ${selectedCoordinate.lng.toFixed(6)}`;
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        <BackButton onClick={props.onClickBackButton} />
        <h1> {latLongString}</h1>
      </div>
      <div className={styles.headerTrailing}>
        <DeleteButton onClick={props.onClickDelete} />
      </div>
    </div>
  );
};

interface DecorationControlProps {
  style: MapLabelDecorationStyle;
  onChangeStyle: (decorationType: MapLabelDecorationStyle) => void;
}

const DecorationControl = (props: DecorationControlProps) => {
  const [isColorPickerExpanded, setIsColorExpanded] = useState(false);
  const availableDecorationTypes: LabelDecoration[] = [
    "none",
    "border",
    "line",
    "bracket",
    "dot",
    "corner",
  ];

  const iconTypeForDecoration = (decoration: LabelDecoration): UIIconName => {
    switch (decoration) {
      case "border":
        return "LABEL-DECORATION-BORDER";
      case "line":
        return "LABEL-DECORATION-LINE";
      case "bracket":
        return "LABEL-DECORATION-BRACKET";
      case "dot":
        return "LABEL-DECORATION-DOT";
      case "corner":
        return "LABEL-DECORATION-CORNER";
      default:
        return "LABEL-DECORATION-NONE";
    }
  };

  const updateDecoration = (style: {
    decorationType?: LabelDecoration;
    color?: string;
    length?: number;
    width?: number;
  }) => {
    const updatedDecoration: MapLabelDecorationStyle = {
      decoration: style.decorationType ?? props.style.decoration,
      color: style.color ?? props.style.color,
      length: style.length ?? props.style.length,
      width: style.width ?? props.style.width,
    };
    props.onChangeStyle(updatedDecoration);
  };

  const selectedColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-selected");

  const interactiveColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-interactive");

  const selctedStyleSupportLength =
    props.style.decoration !== "line" && props.style.decoration !== "dot";

  return (
    <Section title="Label decoration">
      <div className={styles.decorationTypeContainer}>
        {availableDecorationTypes.map((decorationType) => (
          <UIIconButton
            width={40}
            height={40}
            iconName={iconTypeForDecoration(decorationType)}
            onClick={() => {
              const width = props.style.width === 0 ? 0.5 : props.style.width;
              const length =
                props.style.length === 0 ? 0.5 : props.style.length;
              updateDecoration({ decorationType, length: length, width });
            }}
            isSelected={props.style.decoration === decorationType}
            // className={styles.decorationTypeButton}
          />
        ))}
      </div>
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          isExpanded={isColorPickerExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          colors={legendTintColors}
          selectedColor={props.style.color}
          onSelectColor={(color) => {
            updateDecoration({ color });
          }}
        />
        <Slider
          title="Width"
          min={0}
          max={100}
          value={props.style.width * 100}
          onChange={(value) => {
            updateDecoration({ width: value / 100 });
          }}
        />
      </div>
      {selctedStyleSupportLength && (
        <Slider
          title="Length"
          min={0}
          max={100}
          value={props.style.length * 100}
          onChange={(value) => {
            updateDecoration({ length: value / 100 });
          }}
        />
      )}
    </Section>
  );
};

interface BackgroundControlProps {
  backgroundColor: string;
  backgroundOpacity: number;
  padding: number;
  onChangeBackgroundColor: (color: string) => void;
  onChangeBackgroundOpacity: (opacity: number) => void;
  onChangePadding: (padding: number) => void;
}

const BackgroundControl = (props: BackgroundControlProps) => {
  const { project, isTemplateEditing } = usePosterProject();

  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Background">
      <div className={styles.toolboxRowContainer}>
        {(isTemplateEditing ||
          project.editingPermissions.mapLabel.backgroundColor) && (
          <ColorPicker
            showSwatch={true}
            colors={legendTintColors}
            selectedColor={props.backgroundColor}
            onSelectColor={props.onChangeBackgroundColor}
            isExpanded={isColorExpanded}
            onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          />
        )}
        {(isTemplateEditing ||
          project.editingPermissions.mapLabel.backgroundOpacity) && (
          <Slider
            title="Opacity"
            min={0}
            max={100}
            value={props.backgroundOpacity * 100}
            onChange={(opacityPerc) => {
              const opacity = opacityPerc / 100;
              props.onChangeBackgroundOpacity(opacity);
            }}
          />
        )}
      </div>
      {(isTemplateEditing ||
        project.editingPermissions.mapLabel.backgroundPadding) && (
        <Slider
          title="Padding"
          min={0}
          max={20}
          value={props.padding}
          onChange={(value) => {
            props.onChangePadding(value);
          }}
        />
      )}
    </Section>
  );
};

interface PointerControlProps {
  color: string;
  thickness: number;
  onChangeColor: (color: string) => void;
  onChangeThickness: (opacity: number) => void;
}

const PointerControl = (props: PointerControlProps) => {
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Pointer">
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          colors={legendTintColors}
          selectedColor={props.color}
          onSelectColor={props.onChangeColor}
          isExpanded={isColorExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
        />
        <Slider
          title="Width"
          min={0}
          max={10}
          value={props.thickness}
          onChange={(value) => {
            props.onChangeThickness(value);
          }}
        />
      </div>
    </Section>
  );
};

interface TextStyleControlProps {
  font: string;
  textColor: string;
  fontSize: number;
  onFontChange: (font: string) => void;
  onChangeTextColor: (color: string) => void;
  onChangeFontSize: (fontSize: number) => void;
}

const TextStyleControl = (props: TextStyleControlProps) => {
  const { project, isTemplateEditing } = usePosterProject();
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Text style">
      {(isTemplateEditing || project.editingPermissions.mapLabel.font) && (
        <FontPicker
          fontNames={fontNames()}
          selectedFont={props.font ?? "Arial"}
          onSelectFont={(fontName) => {
            props.onFontChange(fontName);
          }}
          className={styles.fontPickerLabel}
        />
      )}

      <div className={styles.toolboxRowContainer}>
        {(isTemplateEditing || project.editingPermissions.mapLabel.color) && (
          <ColorPicker
            showSwatch={true}
            colors={legendTintColors}
            selectedColor={props.textColor}
            onSelectColor={props.onChangeTextColor}
            isExpanded={isColorExpanded}
            onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          />
        )}
        {(isTemplateEditing || project.editingPermissions.mapLabel.size) && (
          <Slider
            title="Size"
            min={8}
            max={48}
            value={props.fontSize}
            onChange={(value) => {
              props.onChangeFontSize(value);
            }}
          />
        )}
      </div>
    </Section>
  );
};

interface TextContentControlProps {
  text: string;
  onChangeText: (text: string) => void;
  coordinate: Coordinate;
  onClickDeleteLabel: () => void;
}

const TextContentControl = (props: TextContentControlProps) => {
  return (
    <Section>
      <div className={styles.toolboxRowContainer}>
        <ToolboxComponentInput
          text={props.text}
          onChangeText={props.onChangeText}
          autoFocusOnMount={true}
        />
      </div>
      <ToolboxReverseGeocodeSuggestions
        coordinate={props.coordinate}
        onSelectLandmarkText={props.onChangeText}
      />
    </Section>
  );
};
